import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { getImageUrl } from '../../config/apiConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { getArabicDate } from '../../services/utils/date';
import Footer from './Footer';
import {
  Home,
  Users,
  Settings,
  LogOut,
  Bell,
  Menu,
  X,
  User,
  GraduationCap,
  Shield,
  CreditCard,
  FileText,
  Calendar,
  MessageSquare,
  Database,
  UserCheck,
  ChevronLeft,
  Search,
  ShieldCheck,
  Clock,
  Zap,
  HelpCircle,
  Monitor,
  Video,
  DollarSign,
  Target,
  BookMarked,
  PieChart
} from 'lucide-react';

// ⭐ Navigation arrays - تحريكها خارج المكون لتجنب إعادة الإنشاء في كل render
const NAVIGATION = [
  { name: 'الرئيسية', href: '/', icon: Home, roles: ['admin', 'teacher', 'student'] },
  { name: 'البحث عن المدرسين', href: '/teachers', icon: Users, roles: ['admin', 'teacher', 'student'] },
  { name: 'نظرة عامة', href: '/dashboard', icon: Monitor, roles: ['admin', 'teacher', 'student'] },
];

const ADMIN_NAVIGATION = [
  { name: 'لوحة التحكم', href: '/admin', icon: Shield, roles: ['admin'] },
  { name: 'إدارة المستخدمين', href: '/admin/users', icon: Users, roles: ['admin'] },
  { name: 'إدارة المحتوى', href: '/admin/content', icon: FileText, roles: ['admin'] },
  { name: 'الموافقة على المدرسين', href: '/admin/teacher-approval', icon: UserCheck, roles: ['admin'] },
  { name: 'إدارة الاشتراكات', href: '/admin/subscriptions', icon: DollarSign, roles: ['admin'] },
  { name: 'نظام الأكواد', href: '/admin/subscription-management', icon: Database, roles: ['admin'] },
  { name: 'التقارير والإحصائيات', href: '/admin/reports', icon: PieChart, roles: ['admin'] },
];

const TEACHER_NAVIGATION = [
  { name: 'لوحة التحكم', href: '/teacher', icon: Monitor, roles: ['teacher'] },
  { name: 'مكتبة دروسي', href: '/teacher/lessons', icon: Video, roles: ['teacher'] },
  { name: 'إدارة الكورسات', href: '/teacher/courses', icon: BookMarked, roles: ['teacher'] },
  { name: 'الطلاب المشتركين', href: '/teacher/students', icon: Users, roles: ['teacher'] },
  { name: 'تخصصاتي', href: '/teacher/subjects', icon: Target, roles: ['teacher'] },
  { name: 'الجدول الزمني', href: '/teacher/schedule', icon: Calendar, roles: ['teacher'] },
  { name: 'إدارة الجدول', href: '/teacher/manage-schedule', icon: Clock, roles: ['teacher'] },
  { name: 'مركز المراسلة', href: '/teacher/messages', icon: MessageSquare, roles: ['teacher'] },
  { name: 'أسئلة الطلاب', href: '/teacher/questions', icon: HelpCircle, roles: ['teacher'] },
  { name: 'إدارة الاختبارات', href: '/teacher/quizzes', icon: FileText, roles: ['teacher'] },
  { name: 'سجل الإيرادات', href: '/teacher/revenue', icon: DollarSign, roles: ['teacher'] },
  { name: 'تقارير الأداء', href: '/teacher/reports', icon: PieChart, roles: ['teacher'] },
];

const STUDENT_NAVIGATION = [
  { name: 'لوحة التحكم', href: '/student', icon: Monitor, roles: ['student'] },
  { name: 'استكشف جديداً', href: '/student/browse', icon: Search, roles: ['student'] },
  { name: 'دروسي الحالية', href: '/student/lessons', icon: Video, roles: ['student'] },
  { name: 'كورساتي المشترك بها', href: '/student/courses', icon: BookMarked, roles: ['student'] },
  { name: 'الجدول الدراسي', href: '/student/schedule', icon: Calendar, roles: ['student'] },
  { name: 'أسئلتي وإجاباتي', href: '/student/questions', icon: HelpCircle, roles: ['student'] },
  { name: 'مركز المراسلة', href: '/student/messages', icon: MessageSquare, roles: ['student'] },
  { name: 'فواتيري والاشتراكات', href: '/student/subscriptions', icon: CreditCard, roles: ['student'] },
  { name: 'تأكيد الدفعات', href: '/student/payment-confirmation', icon: DollarSign, roles: ['student'] },
  { name: 'طلبات الدفع', href: '/student/payment-requests', icon: FileText, roles: ['student'] },
  { name: 'تفعيل اشتراك بالكود', href: '/student/activate-code', icon: Zap, roles: ['student'] },
];

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { getPlatformName } = useSettings();
  const { notifications, unreadCount, markAsRead, markAllAsRead, refreshNotifications, loading } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get platform name from settings for dynamic page title
  const platformName = getPlatformName();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // دالة للتحقق من النشطة (Active state) - مع useCallback لتجنب إعادة الإنشاء
  const isActive = useCallback((href) => {
  return location.pathname === href;
}, [location.pathname]);

  // مزامنة filteredNavigation مع تغير الـ role
  const filteredNavigation = useMemo(() => 
    NAVIGATION.filter(item => item.roles.includes(user?.role)),
    [user?.role]
  );

  // حساب الصفحة الحالية باستخدام useMemo لتحسين الأداء
  const currentPage = useMemo(() => {
    return (
      filteredNavigation.find(item => isActive(item.href))?.name ||
      ADMIN_NAVIGATION.find(item => isActive(item.href))?.name ||
      TEACHER_NAVIGATION.find(item => isActive(item.href))?.name ||
      STUDENT_NAVIGATION.find(item => isActive(item.href))?.name ||
      ''
    );
  }, [filteredNavigation, isActive]);

  // إغلاق القائمة الجانبية عند تغيير المسار في الموبايل
  useEffect(() => {
    setSidebarOpen(false);
    // Scroll to top when route changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  // تحديث عنوان الصفحة ديناميكياً من الإعدادات (اسم الصفحة | اسم المنصة)
  useEffect(() => {
    if (platformName) {
      document.title = currentPage
        ? `${currentPage} | ${platformName}`
        : platformName;
    }
  }, [platformName, currentPage]);

  return (
      <div className="min-h-screen bg-gray-50/50 flex overflow-x-hidden" dir="rtl">
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm lg:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - القائمة الجانبية المطورة */}
      <aside className={`
  fixed inset-y-0 right-0 z-[200] w-72 bg-white shadow-2xl
  transform transition-all duration-500 ease-in-out
  border-l border-gray-100
  lg:static lg:translate-x-0
  ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}
`}>

        <div className="flex flex-col h-full">
          
          {/* Brand Logo Section */}
          <div className="relative overflow-hidden flex items-center justify-between h-24 px-6 border-b border-gray-50 bg-white">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-12 h-12 bg-primary-600 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-200 group-hover:rotate-12 transition-transform duration-300">
                <GraduationCap className="w-7 h-7 text-white" />
              </div>
              <div>
                <span className="text-xl font-black text-gray-900 tracking-tight block leading-none">
                  {platformName}
                </span>
                <span className="text-[10px] font-bold text-primary-500 uppercase tracking-widest mt-1 block">الجيل الذكي للتعلم</span>
              </div>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-xl text-gray-400 hover:bg-gray-50 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Main Navigation Links */}
          <nav className="flex-1 px-4 py-8 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200">
            
            <div className="px-4 mb-4">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">القائمة العامة</p>
            </div>

            {filteredNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  group flex items-center px-4 py-3.5 text-sm font-bold rounded-2xl transition-all duration-300
                  ${isActive(item.href)
                    ? 'bg-primary-600 text-white shadow-xl shadow-primary-200 translate-x-[-4px]'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-primary-600'
                  }
                `}
              >
                <item.icon className={`ml-3 w-5 h-5 transition-transform duration-300 group-hover:scale-110 ${isActive(item.href) ? 'text-white' : 'text-gray-400 group-hover:text-primary-600'}`} />
                {item.name}
              </Link>
            ))}

            {/* Separator and Specific Menus */}
            <div className="pt-8 px-4 pb-4">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">مساحة التحكم</p>
            </div>

            {/* Admin Special Dropdown */}
            {user?.role === 'admin' && (
              <div className="space-y-1">
                <button
                  onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                  className={`
                    w-full group flex items-center justify-between px-4 py-3.5 text-sm font-bold rounded-2xl transition-all
                    ${location.pathname.startsWith('/admin') ? 'bg-gray-900 text-white shadow-xl' : 'text-gray-500 hover:bg-gray-50'}
                  `}
                >
                  <div className="flex items-center">
                    <ShieldCheck className="ml-3 w-5 h-5" />
                    الإدارة المركزية
                  </div>
                  <ChevronLeft className={`w-4 h-4 transition-transform duration-300 ${adminMenuOpen ? '-rotate-90' : ''}`} />
                </button>
                
                {adminMenuOpen && (
                  <div className="mr-4 pr-4 border-r-2 border-gray-100 space-y-1 animate-in slide-in-from-top-2 duration-300">
                    {ADMIN_NAVIGATION.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`
                          flex items-center px-4 py-3 text-xs font-bold rounded-xl transition-all
                          ${isActive(item.href) ? 'text-primary-600 bg-primary-50' : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'}
                        `}
                      >
                        <item.icon className="ml-3 w-4 h-4" />
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Teacher Specific Menu */}
            {user?.role === 'teacher' && TEACHER_NAVIGATION.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  group flex items-center px-4 py-3.5 text-sm font-bold rounded-2xl transition-all duration-300
                  ${isActive(item.href)
                    ? 'bg-blue-600 text-white shadow-xl shadow-blue-100 translate-x-[-4px]'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-blue-600'
                  }
                `}
              >
                <item.icon className={`ml-3 w-5 h-5 transition-transform duration-300 group-hover:scale-110 ${isActive(item.href) ? 'text-white' : 'text-gray-400 group-hover:text-blue-600'}`} />
                {item.name}
              </Link>
            ))}

            {/* Student Specific Menu */}
            {user?.role === 'student' && STUDENT_NAVIGATION.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  group flex items-center px-4 py-3.5 text-sm font-bold rounded-2xl transition-all duration-300
                  ${isActive(item.href)
                    ? 'bg-emerald-600 text-white shadow-xl shadow-emerald-100 translate-x-[-4px]'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-emerald-600'
                  }
                `}
              >
                <item.icon className={`ml-3 w-5 h-5 transition-transform duration-300 group-hover:scale-110 ${isActive(item.href) ? 'text-white' : 'text-gray-400 group-hover:text-emerald-600'}`} />
                {item.name}
              </Link>
            ))}

            {/* Help & Support Section */}
            <div className="pt-8 px-4 pb-4">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">الدعم والمساعدة</p>
            </div>
            
            <Link
              to="/help"
              className={`
                group flex items-center px-4 py-3.5 text-sm font-bold rounded-2xl transition-all duration-300
                ${isActive('/help')
                  ? 'bg-purple-600 text-white shadow-xl shadow-purple-100 translate-x-[-4px]'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-purple-600'
                }
              `}
            >
              <HelpCircle className={`ml-3 w-5 h-5 transition-transform duration-300 group-hover:scale-110 ${isActive('/help') ? 'text-white' : 'text-gray-400 group-hover:text-purple-600'}`} />
              مركز المساعدة
            </Link>
          </nav>

          {/* User Section (Profile & Actions) */}
          <div className="p-4 border-t border-gray-50 bg-gray-50/50">
            <div className="bg-white p-4 rounded-3xl border border-gray-100 shadow-sm space-y-4">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center shadow-lg border-2 border-white overflow-hidden">
                        {user?.avatar ? <img src={getImageUrl(user.avatar)} className="w-full h-full object-cover" alt="" /> : <User className="w-6 h-6 text-white" />}
                    </div>
                    <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-sm"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black text-gray-900 truncate">{user?.name}</p>
                    <p className="text-[10px] font-bold text-gray-400 uppercase">{user?.role === 'admin' ? 'مدير النظام' : user?.role === 'teacher' ? 'مدرس معتمد' : 'طالب'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 border-t border-gray-50 pt-4">
                    <Link to="/profile" className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-primary-50 text-gray-400 hover:text-primary-600 transition-all">
                        <User size={16} />
                        <span className="text-[8px] font-black uppercase">بروفايل</span>
                    </Link>
                    <Link to="/settings" className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-primary-50 text-gray-400 hover:text-primary-600 transition-all">
                        <Settings size={16} />
                        <span className="text-[8px] font-black uppercase">إعدادات</span>
                    </Link>
                    <button onClick={handleLogout} className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-red-50 text-gray-400 hover:text-red-600 transition-all">
                        <LogOut size={16} />
                        <span className="text-[8px] font-black uppercase">خروج</span>
                    </button>
                </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Side */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        
        {/* Modern Top Header Bar */}
        <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 h-20 sticky top-0 z-[100]">
          <div className="flex items-center justify-between h-full px-6 lg:px-10">
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2.5 rounded-2xl bg-gray-50 text-gray-500 hover:bg-primary-50 hover:text-primary-600 transition-all"
              >
                <Menu size={24} />
              </button>
              <div className="hidden sm:block">
                <h1 className="text-xl font-black text-gray-900 tracking-tight">
                  {currentPage || platformName}
                </h1>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-0.5">
                    {platformName} <ChevronLeft size={10} className="inline-block mx-1"/> {user?.role === 'admin' ? 'المدير' : user?.role === 'teacher' ? 'المدرس' : 'الطالب'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              
              {/* Notification Center */}
              <div className="relative">
                <button
                  onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
                  className={`p-3 rounded-2xl transition-all relative ${
                    notificationDropdownOpen 
                      ? 'bg-primary-600 text-white shadow-lg shadow-primary-100' 
                      : unreadCount > 0 
                        ? 'bg-gradient-to-br from-red-500 to-pink-500 text-white shadow-lg shadow-red-100 animate-pulse' 
                        : 'bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                  }`}
                >
                  <Bell size={22} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-red-600 to-pink-600 border-2 border-white rounded-full flex items-center justify-center text-[10px] font-black text-white shadow-lg animate-bounce">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {notificationDropdownOpen && (
                  <div className="absolute left-0 mt-4 w-[350px] bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-gray-100 z-[200] overflow-hidden animate-in fade-in zoom-in-95 duration-300">
                    <div className="p-6 border-b border-gray-50 bg-gray-50/50 flex items-center justify-between">
                        <div>
                            <h3 className="text-base font-black text-gray-900">مركز التنبيهات</h3>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">لديك {unreadCount} إشعار غير مقروء</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button 
                                onClick={refreshNotifications} 
                                disabled={loading}
                                className="text-[10px] font-black text-primary-600 hover:underline uppercase flex items-center gap-1 disabled:opacity-50"
                            >
                                {loading ? 'جاري التحديث...' : 'تحديث'}
                            </button>
                            {unreadCount > 0 && (
                                <button onClick={markAllAsRead} className="text-[10px] font-black text-primary-600 hover:underline uppercase">تحديد الكل</button>
                            )}
                        </div>
                    </div>
                    
                    <div className="max-h-[400px] overflow-y-auto p-2">
                      {notifications.length === 0 ? (
                        <div className="py-12 text-center">
                          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Bell className="text-gray-200" size={32} />
                          </div>
                          <p className="text-sm font-bold text-gray-400">لا توجد إشعارات جديدة حالياً</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                            {notifications.map((notification) => (
                            <div
                                key={notification.id}
                                className={`p-4 rounded-2xl transition-all group relative ${!notification.is_read ? 'bg-primary-50/50 border border-primary-50' : 'hover:bg-gray-50'}`}
                            >
                                <div className="flex gap-4">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${!notification.is_read ? 'bg-primary-600 text-white shadow-lg shadow-primary-100' : 'bg-gray-100 text-gray-400'}`}>
                                        <Zap size={18} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-black text-gray-900">{notification.title}</p>
                                        <p className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-2">{notification.message}</p>
                                        <div className="flex items-center justify-between mt-3">
                                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1">
                                                <Clock size={10} /> {getArabicDate(notification.created_at)}
                                            </span>
                                            {!notification.is_read && (
                                                <button onClick={() => markAsRead(notification.id)} className="text-[9px] font-black text-primary-600 hover:text-primary-800 uppercase bg-white px-2 py-1 rounded-lg border border-primary-100 shadow-sm">تحديد كمقروء</button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Quick Avatar */}
              <Link to="/profile" className="lg:hidden w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center overflow-hidden">
                {user?.avatar ? <img src={getImageUrl(user.avatar)} className="w-full h-full object-cover" alt="" /> : <User size={20} className="text-primary-600" />}
              </Link>

            </div>
          </div>
        </header>

        {/* Dynamic Page Content Section */}
        <main className="flex-1 overflow-y-auto scroll-smooth bg-gray-50/50 p-4 sm:p-6 lg:p-10">
          <div className="max-w-7xl mx-auto animate-in fade-in duration-700">
            {children}
            <Outlet key={location.pathname} />
          </div>
        </main>

        {/* Global Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default Layout;